import { format, parseISO } from 'date-fns';
import { Download, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import { useNfseDownload } from '@/features/nfse/hooks';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import type { NfseIssuance } from '@/features/fiscal/schemas';
import { friendlyRejectionMessage } from '@/features/fiscal/nfseRejection';
import { RetryNfseButton } from './RetryNfseButton';

const STATUS_LABEL: Record<NfseIssuance['status'], string> = {
  QUEUED: 'Em fila',
  SENT: 'Enviada',
  ACCEPTED: 'Aceita',
  REJECTED: 'Rejeitada',
  CANCELED: 'Cancelada',
  FAILED: 'Falhou',
};

const STATUS_CLASS: Record<NfseIssuance['status'], string> = {
  QUEUED: 'text-amber-700 dark:text-amber-300',
  SENT: 'text-sky-700 dark:text-sky-300',
  ACCEPTED: 'text-emerald-700 dark:text-emerald-300',
  REJECTED: 'text-red-700 dark:text-red-300',
  CANCELED: 'text-zinc-700 dark:text-zinc-300',
  FAILED: 'text-red-700 dark:text-red-300',
};

interface Props {
  issuances: NfseIssuance[];
  onRetry?: () => Promise<unknown> | void;
  isRetrying?: boolean;
  retryDisabled?: boolean;
  retryDisabledReason?: string;
}

export function NfseHistoryList({
  issuances,
  onRetry,
  isRetrying,
  retryDisabled,
  retryDisabledReason,
}: Props) {
  const toast = useToast();
  const download = useNfseDownload();

  // Permite retentar tanto falhas transientes (FAILED) quanto rejeições do
  // provedor por campo corrigível (REJECTED) — ex.: código de serviço inválido.
  // O backend só exige que o registro siga em MATCHED. Rejeições de autorização
  // (E347 — prestador não credenciado) são definitivas: não oferecemos retry,
  // pois só o credenciamento na prefeitura/Emissor Nacional as resolve.
  const latest = issuances[0];
  const latestRejection = latest ? friendlyRejectionMessage(latest.errorPayload) : null;
  const showRetry =
    onRetry &&
    issuances.length > 0 &&
    (latest.status === 'FAILED' || latest.status === 'REJECTED') &&
    latestRejection?.kind !== 'AUTHORIZATION';

  const handleDownload = async (
    id: string,
    kind: 'xml' | 'pdf',
  ): Promise<void> => {
    try {
      const { url } = await download.mutateAsync({ issuanceId: id, kind });
      // Abrimos a signed URL em nova aba — o token do app não é exposto
      // porque a URL já é pré-assinada pelo GCS.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : 'Falha ao gerar link de download';
      toast.show({ title: msg, variant: 'error' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Histórico de NFS-e</CardTitle>
      </CardHeader>

      {issuances.length === 0 ? (
        <p className="text-sm text-(--color-fg-muted)">
          Nenhuma emissão registrada ainda.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--color-border)">
          {issuances.map((i) => {
            const rejection =
              i.status === 'REJECTED' || i.status === 'FAILED'
                ? friendlyRejectionMessage(i.errorPayload)
                : null;
            return (
            <li key={i.id} className="flex flex-wrap items-center gap-3 py-3">
              <FileText className="h-4 w-4 text-(--color-fg-muted)" aria-hidden />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {i.nfseNumber ? `NFS-e #${i.nfseNumber}` : 'Tentativa sem número'}
                </p>
                <p className="text-xs text-(--color-fg-muted)">
                  {format(parseISO(i.createdAt), 'dd/MM/yyyy HH:mm')}
                  {i.retryCount > 0 ? ` · ${i.retryCount} retry(s)` : ''}
                </p>
              </div>
              <span className={cn('text-xs font-medium', STATUS_CLASS[i.status])}>
                {STATUS_LABEL[i.status]}
              </span>
              {i.pdfGcsPath ? (
                <button
                  type="button"
                  onClick={() => handleDownload(i.id, 'pdf')}
                  className="inline-flex items-center gap-1 text-xs text-(--color-accent) hover:underline"
                  aria-label={`Baixar PDF da NFS-e ${i.nfseNumber ?? ''}`}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden /> PDF
                </button>
              ) : null}
              {i.xmlGcsPath ? (
                <button
                  type="button"
                  onClick={() => handleDownload(i.id, 'xml')}
                  className="inline-flex items-center gap-1 text-xs text-(--color-accent) hover:underline"
                  aria-label={`Baixar XML da NFS-e ${i.nfseNumber ?? ''}`}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden /> XML
                </button>
              ) : null}
              {rejection ? (
                <div
                  className={cn(
                    'w-full rounded-md border px-3 py-2 text-xs',
                    rejection.kind === 'AUTHORIZATION'
                      ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
                      : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
                  )}
                >
                  <p className="font-medium">{rejection.title}</p>
                  <p className="mt-0.5">{rejection.hint}</p>
                </div>
              ) : null}
            </li>
            );
          })}
        </ul>
      )}

      {showRetry ? (
        <div className="mt-3">
          <RetryNfseButton
            onRetry={onRetry}
            isRetrying={isRetrying}
            disabled={retryDisabled}
          />
          {retryDisabled && retryDisabledReason ? (
            <p className="mt-1 text-xs text-(--color-fg-muted)">{retryDisabledReason}</p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
